import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Search as SearchIcon, Download as DownloadIcon } from '@mui/icons-material';
import { reportService, UserCheckinReportEntry } from '../services/reportService';
import { exportToCSV } from '../utils/csvExport';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface User {
  id: number;
  name: string;
  email: string;
}

const UserCheckinReport: React.FC = () => {
  const { user } = useAuth();
  
  // Check if user can view all users (PMP Admin, Site Admin, or Client Manager)
  const canViewAll = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => 
      ['pmp_admin', 'site_admin', 'client_manager'].includes(role.slug)
    );
  };

  // Get today's date and 7 days ago as defaults
  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);
  
  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  // Filter state
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [startDate, setStartDate] = useState<string>(formatDate(sevenDaysAgo));
  const [endDate, setEndDate] = useState<string>(formatDate(today));

  // Data state
  const [users, setUsers] = useState<User[]>([]);
  const [reportData, setReportData] = useState<UserCheckinReportEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);

  // Load users on mount (only if user can view all)
  useEffect(() => {
    if (canViewAll()) {
      loadUsers();
    }
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.data || []);
    } catch (err: any) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = {
        start_date: startDate,
        end_date: endDate,
      };
      
      if (canViewAll() && selectedUserId) {
        params.user_id = selectedUserId;
      }

      const data = await reportService.getUserCheckinReport(params);
      setReportData(data);
      setPage(0); // Reset to first page
    } catch (err: any) {
      console.error('Error fetching report:', err);
      setError(err.response?.data?.message || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportCSV = () => {
    type CheckinRow = UserCheckinReportEntry & {
      checkin_est: string;
      checkin_ist: string;
      checkout_est: string;
      checkout_ist: string;
    };
    const rows: CheckinRow[] = reportData.map((e) => ({
      ...e,
      checkin_est: e.checkin_start_est || '—',
      checkin_ist: e.checkin_start_ist || '—',
      checkout_est: e.checkin_end_est || '—',
      checkout_ist: e.checkin_end_ist || '—',
    }));
    exportToCSV<CheckinRow>(
      rows,
      [
        { key: 'user_name', label: 'User' },
        { key: 'checkin_date', label: 'Date' },
        { key: 'checkin_est', label: 'Checkin (EST)' },
        { key: 'checkin_ist', label: 'Checkin (IST)' },
        { key: 'checkout_est', label: 'Checkout (EST)' },
        { key: 'checkout_ist', label: 'Checkout (IST)' },
        { key: 'break_time_formatted', label: 'Break Hours' },
        { key: 'login_hours_formatted', label: 'Login Hours' },
      ],
      `user-checkin-report_${startDate}_to_${endDate}.csv`
    );
  };

  // Calculate pagination
  const paginatedData = reportData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        User Checkin Report
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filter
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { md: 'flex-start' } }}>
          {canViewAll() && (
            <Box sx={{ flex: { xs: 1, md: 2 }, minWidth: { xs: '100%', md: '200px' } }}>
              <FormControl fullWidth>
                <InputLabel>User List</InputLabel>
                <Select
                  value={selectedUserId}
                  label="User List"
                  onChange={(e) => setSelectedUserId(e.target.value as number | '')}
                >
                  <MenuItem value="">--Choose User--</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '150px' } }}>
            <TextField
              fullWidth
              label="Start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="E.g., 2025-10-28"
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '150px' } }}>
            <TextField
              fullWidth
              label="End"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="E.g., 2025-10-28"
            />
          </Box>
          <Box sx={{ minWidth: { xs: '100%', md: '120px' } }}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSearch}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ height: '56px' }}
            >
              Search
            </Button>
          </Box>
          <Box sx={{ minWidth: { xs: '100%', md: '120px' } }}>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              onClick={handleExportCSV}
              disabled={loading || reportData.length === 0}
              startIcon={<DownloadIcon />}
              sx={{ height: '56px' }}
            >
              Export CSV
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Result
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>User</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Checkin</strong></TableCell>
                <TableCell><strong>Checkout</strong></TableCell>
                <TableCell><strong>Break Hours</strong></TableCell>
                <TableCell><strong>Login Hours</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No data available. Please select filters and click Search.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((entry, index) => (
                  <TableRow key={`${entry.user_id}-${entry.checkin_date}-${index}`}>
                    <TableCell>{entry.user_name}</TableCell>
                    <TableCell>{entry.checkin_date}</TableCell>
                    <TableCell>
                      {entry.checkin_start_est ? (
                        <>
                          <div><strong>EST:</strong> {entry.checkin_start_est}</div>
                          <div><strong>IST:</strong> {entry.checkin_start_ist}</div>
                        </>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {entry.checkin_end_est ? (
                        <>
                          <div><strong>EST:</strong> {entry.checkin_end_est}</div>
                          <div><strong>IST:</strong> {entry.checkin_end_ist}</div>
                        </>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{entry.break_time_formatted}</TableCell>
                    <TableCell>{entry.login_hours_formatted}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {reportData.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {page * rowsPerPage + 1} to{' '}
              {Math.min((page + 1) * rowsPerPage, reportData.length)} of {reportData.length} entries
            </Typography>
            <TablePagination
              component="div"
              count={reportData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 30, 50, 100]}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default UserCheckinReport;
